/**
 * User Tier Constants — canonical source of truth for tier identity.
 *
 * Tier language is unified here (Workstream C): every surface should describe the
 * three tiers the same way. The cosmic name leads; a short plain-language
 * descriptor pairs with it.
 *   FREE     = First Light       (your top 3 elements)
 *   ENHANCED = Stellar Map       (your top 8 elements)        $24.99
 *   PREMIUM  = Complete Universe (all 45 elements)            $39.99
 *
 * Pricing note: amounts here are for display only. The server (functions
 * createPaymentIntent / amountForUpgrade) is authoritative for what is charged:
 * Enhanced $24.99, Premium $39.99, Premium-from-Enhanced top-up $14.99.
 */

export const USER_TIERS = {
  FREE: 'free',
  ENHANCED: 'enhanced',
  PREMIUM: 'premium'
};

export const TIER_PRICING = {
  [USER_TIERS.ENHANCED]: 24.99,
  [USER_TIERS.PREMIUM]: 39.99,
  // Discounted top-up when an Enhanced taker upgrades to Premium.
  PREMIUM_FROM_ENHANCED: 14.99
};

export const TIER_FEATURES = {
  [USER_TIERS.FREE]: {
    // `name` is the canonical display name (cosmic). `functionalName` is the
    // internal/legacy label; prefer `name` everywhere user-facing.
    name: 'First Light',
    functionalName: 'Free',
    tagline: 'Your top 3 elements',
    maxElements: 3,
    maxDomains: 2,
    features: [
      'Your constellation pattern',
      'Top 3 motivated elements',
      'Domain strength overview',
      'Basic career suggestions'
    ]
  },
  [USER_TIERS.ENHANCED]: {
    name: 'Stellar Map',
    functionalName: 'Enhanced',
    tagline: 'Your top 8 elements',
    maxElements: 8,
    maxDomains: 5,
    features: [
      'Complete constellation pattern',
      'Top 8 motivated elements with full detail',
      'Interactive constellation explorer',
      'Detailed element descriptions',
      'Enhanced career recommendations'
    ]
  },
  [USER_TIERS.PREMIUM]: {
    name: 'Complete Universe',
    functionalName: 'Premium',
    tagline: 'All 45 elements',
    maxElements: 45,
    maxDomains: 5,
    features: [
      'All Stellar Map features',
      'All 45 motivated elements unlocked',
      'Professional PDF report',
      'Cross-domain element insights',
      'Comprehensive career roadmap'
    ]
  }
};

/**
 * Utility functions for tier management
 */
export const getTierFromStorage = () => {
  return localStorage.getItem('userTier') || USER_TIERS.FREE;
};

export const setTierToStorage = (tier) => {
  localStorage.setItem('userTier', tier);
  localStorage.setItem('tierUpgradeDate', new Date().toISOString());
};

// Canonical cosmic display name. Accepts either the lowercase key ('enhanced')
// or the uppercase form ('ENHANCED').
export const getTierDisplayName = (tier) => {
  const key = String(tier || '').toLowerCase();
  return TIER_FEATURES[key]?.name || tier;
};

// Short plain-language descriptor that pairs with the cosmic name.
export const getTierTagline = (tier) => {
  const key = String(tier || '').toLowerCase();
  return TIER_FEATURES[key]?.tagline || '';
};

export const getTierFeatures = (tier) => {
  const key = String(tier || '').toLowerCase();
  return TIER_FEATURES[key]?.features || [];
};

export const getTierMaxElements = (tier) => {
  const key = String(tier || '').toLowerCase();
  return TIER_FEATURES[key]?.maxElements || 3;
};
